/**
 * Base Cali — global motion layer.
 *
 *   • Native scroll everywhere (ScrollSmoother removed for performance).
 *   • Reveal system driven by [data-reveal] attributes. Triggered by a native
 *     IntersectionObserver (not ScrollTrigger) so it fires reliably on every
 *     device/scroll setup and keeps the JS light. GSAP + SplitText do the motion.
 *   • Card add-to-cart feedback (spinner → "Added").
 *
 * Reveal attributes (put on any element):
 *   data-reveal="words"  → SplitText into words, masked rise, stagger
 *   data-reveal="lines"  → SplitText into lines, masked rise, stagger
 *   data-reveal="clip"   → clip-path wipe, then [data-reveal-after] children fade
 *   data-reveal="fade"   → simple fade + translate (default)
 *   data-reveal-delay    → seconds before it starts (default 0)
 *   data-reveal-stagger  → per-target stagger seconds
 */
import { gsap, SplitText } from 'gsap';

const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// Hide [data-reveal] elements as early as this module runs (they're below the
// fold, so no flash). Kept in a class so no-JS = visible.
document.documentElement.classList.add('reveal-ready');

/* ----------------------------- reveal trigger ------------------------------ */
// One shared observer plays each element's animation when it enters the viewport.
const observer =
  'IntersectionObserver' in window
    ? new IntersectionObserver(
        (entries, obs) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            obs.unobserve(entry.target);
            if (typeof entry.target._revealPlay === 'function') entry.target._revealPlay();
          });
        },
        { rootMargin: '0px 0px -15% 0px', threshold: 0 }
      )
    : null;

function whenInView(el, play) {
  if (observer) {
    el._revealPlay = play;
    observer.observe(el);
  } else {
    play(); // no IntersectionObserver support → just show it
  }
}

/* --------------------------------- reveals --------------------------------- */
function buildReveal(el) {
  if (el.dataset.revealReady) return;
  el.dataset.revealReady = '1';

  const type = el.dataset.reveal || 'fade';
  const delay = parseFloat(el.dataset.revealDelay) || 0;

  if (reduceMotion) {
    gsap.set(el, { opacity: 1 });
    el.querySelectorAll('[data-reveal-after]').forEach((c) => gsap.set(c, { opacity: 1 }));
    return;
  }

  if (type === 'clip') {
    // Wipe the element open top->bottom (clip-path), then reveal any
    // [data-reveal-after] descendants (captions) once the wipe finishes.
    const afters = el.querySelectorAll('[data-reveal-after]');
    gsap.set(el, { opacity: 1 });
    if (afters.length) gsap.set(afters, { opacity: 0, y: 14 });

    const tl = gsap.timeline({ paused: true, delay });
    tl.fromTo(
      el,
      { clipPath: 'inset(0% 0% 100% 0% round 4px)' },
      { clipPath: 'inset(0% 0% 0% 0% round 4px)', duration: 1.05, ease: 'power3.inOut' }
    );
    if (afters.length) {
      tl.to(afters, { opacity: 1, y: 0, duration: 0.55, ease: 'power3.out', stagger: 0.08 }, '-=0.15');
    }
    whenInView(el, () => tl.play());
    return;
  }

  if (type === 'words' || type === 'lines') {
    const split = new SplitText(el, {
      type: type === 'words' ? 'lines,words' : 'lines',
      linesClass: 'sp-line',
      wordsClass: 'sp-word',
    });
    const targets = type === 'words' ? split.words : split.lines;

    gsap.set(el, { opacity: 1 });
    const tween = gsap.fromTo(
      targets,
      { yPercent: 115 },
      {
        yPercent: 0,
        duration: 0.9,
        ease: 'power3.out',
        paused: true,
        delay,
        stagger: parseFloat(el.dataset.revealStagger) || (type === 'words' ? 0.05 : 0.12),
        // Remove the overflow-hidden line masks once revealed so descenders
        // (g, y, italics) aren't clipped — works for any heading font.
        onComplete: () => split.revert(),
      }
    );
    whenInView(el, () => tween.play());
  } else {
    const tween = gsap.fromTo(
      el,
      { y: 26, opacity: 0 },
      { y: 0, opacity: 1, duration: 0.9, ease: 'power3.out', delay, paused: true }
    );
    whenInView(el, () => tween.play());
  }
}

function scan(root = document) {
  root.querySelectorAll('[data-reveal]').forEach((el) => {
    try {
      buildReveal(el);
    } catch (err) {
      // Never let one element's failure hide it (or stop the others).
      forceShow(el);
    }
  });
}

// Fail-safe: make every reveal element visible no matter what (used if the
// engine can't initialise on a given device/browser).
function forceShow(scope) {
  const root = scope || document;
  if (root.hasAttribute && root.hasAttribute('data-reveal')) {
    root.style.opacity = '1';
    root.style.transform = 'none';
    root.style.clipPath = 'none';
  }
  root.querySelectorAll('[data-reveal], [data-reveal-after]').forEach((el) => {
    el.style.opacity = '1';
    el.style.transform = 'none';
    el.style.clipPath = 'none';
  });
}

/* --------------------------------- carousels -------------------------------- */
// Wire prev/next arrows for any [data-carousel] (a track + optional buttons).
function initCarousels(root = document) {
  root.querySelectorAll('[data-carousel]').forEach((carousel) => {
    if (carousel.dataset.carouselReady) return;
    carousel.dataset.carouselReady = '1';

    const track = carousel.querySelector('[data-carousel-track]');
    if (!track) return;
    const prev = carousel.querySelector('[data-carousel-prev]');
    const next = carousel.querySelector('[data-carousel-next]');

    const stepSize = () => {
      const card = track.firstElementChild;
      const styles = getComputedStyle(track);
      const gap = parseFloat(styles.columnGap || styles.gap) || 16;
      return card ? card.getBoundingClientRect().width + gap : track.clientWidth * 0.8;
    };

    const update = () => {
      const max = track.scrollWidth - track.clientWidth - 4;
      if (prev) prev.disabled = track.scrollLeft <= 4;
      if (next) next.disabled = track.scrollLeft >= max;
    };

    if (prev) prev.addEventListener('click', () => track.scrollBy({ left: -stepSize(), behavior: 'smooth' }));
    if (next) next.addEventListener('click', () => track.scrollBy({ left: stepSize(), behavior: 'smooth' }));
    track.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    update();
  });
}

/* ------------------------------ card add-to-cart ---------------------------- */
// Spinner the instant Add-to-bag is clicked on a card, then an "Added" state.
// Reacts to Horizon's cart-update event, with a timeout fallback.
function setupCardAdd(root = document) {
  root.querySelectorAll('product-form-component').forEach((pfc) => {
    if (pfc.dataset.cardAddReady) return;
    const btn = pfc.querySelector('[data-card-add]');
    const form = pfc.querySelector('form');
    if (!btn || !form) return;
    pfc.dataset.cardAddReady = '1';

    let resetTimer;
    let fallbackTimer;
    const reset = () => btn.classList.remove('is-loading', 'is-added');
    const showAdded = () => {
      if (!btn.classList.contains('is-loading')) return;
      btn.classList.remove('is-loading');
      btn.classList.add('is-added');
      clearTimeout(resetTimer);
      resetTimer = setTimeout(reset, 1700);
    };

    form.addEventListener('submit', () => {
      if (btn.disabled) return;
      btn.classList.remove('is-added');
      btn.classList.add('is-loading');
      clearTimeout(fallbackTimer);
      fallbackTimer = setTimeout(showAdded, 1200);
    });

    ['cart:lines:update', 'cart:lines:add', 'cart:update', 'cartLinesUpdate', 'cart:refresh'].forEach((name) => {
      document.addEventListener(name, () => {
        clearTimeout(fallbackTimer);
        showAdded();
      });
    });
  });
}

/* ----------------------------------- boot ---------------------------------- */
function boot() {
  // Build reveals after fonts load (so SplitText line breaks are correct), but
  // never wait forever — document.fonts.ready can hang on some Android browsers
  // / in-app WebViews, which would leave [data-reveal] content stuck hidden.
  let started = false;
  const run = () => {
    if (started) return;
    started = true;
    try {
      scan();
      initCarousels();
      setupCardAdd();
    } catch (err) {
      forceShow(document);
    }
  };

  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(run);
  }
  // Backstop: run even if fonts.ready never resolves.
  setTimeout(run, 1000);
}

if (document.readyState !== 'loading') {
  boot();
} else {
  document.addEventListener('DOMContentLoaded', boot);
}

// Re-scan sections re-rendered in the theme editor.
document.addEventListener('shopify:section:load', (event) => {
  scan(event.target);
  initCarousels(event.target);
  setupCardAdd(event.target);
});
